import classNames from "classnames";
import React, { Dispatch, FC, SetStateAction } from "react";
import { toast } from "react-toastify";

import Button from "../../../components/button/Button";
import { Email } from "../../../factories/emails/models/emails.models";
import { useAppSelector } from "../../../redux/redux";
import { useDeleteEmailMutation } from "../../../slices/EmailsSlice";
import { deleteEmailText, deleteFailureText } from "./deleteEmail.utils";

type Props = {
  isActive: boolean;
  onClose: () => void;
  emailId: string;
  /** The email is in the Deleted folder: deleting removes it for good. */
  permanent?: boolean;
  setSelectedMail?: Dispatch<SetStateAction<Email | null>>;
};

/**
 * Delete confirmation for one email (QA batch 19, MAIL-014): a real sentence
 * instead of "Delete? / Message text", and the server's answer is shown, so an
 * email the mailbox provider kept in Deleted is no longer reported as deleted.
 */
export const DeleteEmailModal: FC<Props> = ({
  isActive,
  onClose,
  emailId,
  permanent,
  setSelectedMail,
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const [deleteEmail, { isLoading }] = useDeleteEmailMutation();
  const text = deleteEmailText(!!permanent);

  const confirm = async () => {
    try {
      const answer = await deleteEmail({
        userId: user.id,
        messageId: emailId,
      }).unwrap();
      toast.success(
        (answer as { permanently?: boolean } | null)?.permanently
          ? "Email deleted permanently."
          : "Email moved to Deleted.",
      );
      onClose();
      if (setSelectedMail) setSelectedMail(null);
    } catch (error) {
      toast.error(deleteFailureText(error), { toastId: "delete-email-failed" });
      onClose();
    }
  };

  return (
    <div
      className={classNames("modal", isActive ? "modal-open" : "modal-close")}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        className={"modal-box w-fit min-w-[300px] max-w-[min(90%,440px)]"}>
        <div className={"mb-4 text-xl font-semibold text-gray-800"}>
          {text.title}
        </div>
        <div className={"mb-4 text-base text-gray-800"}>{text.body}</div>
        <div className={"flex justify-between gap-4"}>
          <Button label={"Cancel"} colorType={"outline"} onClick={onClose} />
          <Button
            label={text.confirm}
            isLoading={isLoading}
            disabled={isLoading}
            onClick={confirm}
          />
        </div>
      </div>
    </div>
  );
};
